import styles from "./ImageSearch.module.scss";
import classNames from "classnames";
import { ImageUploadIcon, VisualSimilarityButtonIcon } from "../Icons";
import VLButton from "../../../../../../uikit/Buttons/VLButton";
import {
  ChangeEvent,
  createRef,
  DragEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useImageUpload } from "../../../../../../../hooks/useImageUpload";
import Cropper, { ReactCropperElement } from "react-cropper";
import { validateImageUpload } from "../../../../../../../helpers/validations";
import { App } from "antd";
import { useSelector } from "react-redux";
import { getIsCardslistLoading } from "../../../../../../../redux/SingleDataset/selectors";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { VQL_EXCLUDE, VQL_UPGRADE } from "helpers/constants/filters";
import VLCropper, { VLCropperRef } from "views/uikit/VLCropper";
import IconButton from "../../../../../../uikit/Buttons/IconButton";
import { useHotkeys } from "react-hotkeys-hook";

type ImageSearchProps = {
  className?: string;
  onUpload: () => void;
  initialFile?: File | null;
};

const preventDefault = (fn: Function) => (e: MouseEvent) => {
  e.preventDefault();
  fn(e);
};

const Upload = ({ setFile }: { setFile: (f: File) => void }) => {
  const {
    notification: { error },
  } = App.useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOver, setIsOver] = useState(false);
  const handleFile = useCallback(
    (file?: File) => {
      if (!file) {
        return;
      }
      if (validateImageUpload(file)) {
        setFile(file);
      } else {
        error({ message: "Invalid file" });
      }
    },
    [setFile, error],
  );
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    handleFile(e.dataTransfer.files[0]);
  };
  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
  };

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.items || [])
        .find((item) => item.type.startsWith("image/"))
        ?.getAsFile();

      if (!file) return;

      e.preventDefault();
      handleFile(file);
    },
    [handleFile],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      window[entry.isIntersecting ? "addEventListener" : "removeEventListener"](
        "paste",
        handlePaste as any,
      );
    });

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      window.removeEventListener("paste", handlePaste as any);
    };
  }, [handlePaste]);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.upload, styles.border, {
        [styles.over]: isOver,
      })}
      onDragOver={preventDefault(() => setIsOver(true))}
      onDragLeave={preventDefault(() => setIsOver(false))}
      onDrop={preventDefault(handleDrop)}
    >
      <ImageUploadIcon />
      <div>
        <div className={styles.mainText}>
          Drag and drop Image or click to upload
        </div>
        <div className={styles.subText}>
          Supported Formats: JPG, JPEG, PNG, GIF, JFIF, WEBP, BMP
        </div>
      </div>
      <VLButton
        type="primary"
        className={styles.browseButton}
        size="middle"
        onClick={() => inputRef.current?.click()}
      >
        Browse Images
      </VLButton>
      <input ref={inputRef} type="file" hidden onChange={handleFileInput} />
    </div>
  );
};

const Crop = ({
  file,
  handleVisualSearch,
}: {
  file: File;
  handleVisualSearch: ReturnType<typeof useImageUpload>["handleVisualSearch"];
}) => {
  const isLoading = useSelector(getIsCardslistLoading);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const image = useMemo(() => {
    return URL.createObjectURL(file);
  }, [file]);
  const vlCropperRef = useRef<VLCropperRef>(null);
  const cropperRef = createRef<ReactCropperElement>();
  const isVQLUpgradeEnabled = useFeatureFlagService(VQL_UPGRADE).enabled;
  const isVqlExcludeEnabled = useFeatureFlagService(VQL_EXCLUDE).enabled;

  // Function for when VQL is enabled
  const getCropDataVQL = () => {
    const cropResult = vlCropperRef.current?.getCropData();
    if (cropResult?.cropData) {
      const { x, y, width, height } = cropResult.cropData!;
      const absoluteHeightDifference = Math.abs(
        height - imageDimensions.height,
      );
      const absoluteWidthDifference = Math.abs(width - imageDimensions.width);
      const sameHeight =
        absoluteHeightDifference > 0 && absoluteHeightDifference <= 1;
      const sameWidth =
        absoluteWidthDifference > 0 && absoluteWidthDifference <= 1;
      let interacted = false;
      if (!sameHeight || !sameWidth) interacted = true;

      handleVisualSearch(
        {
          x: Math.round(x),
          y: Math.round(y),
          x1: Math.round(width) + Math.round(x),
          y1: Math.round(height) + Math.round(y),
          file: file,
          interacted,
        },
        { flatResponse: true },
      );
    }
  };

  // Function for when VQL is disabled
  const getCropData = () => {
    if (cropperRef.current?.cropper) {
      const croppedCanvas = cropperRef.current!.cropper!.getCroppedCanvas();
      croppedCanvas.toBlob((blob: any) => {
        const newFile = new File([blob], file.name, {
          type: "image/jpeg",
        });
        // Update state with the cropped file
        const { x, y, width, height } = cropperRef.current?.cropper.getData()!;
        const absoluteHeightDifference = Math.abs(
          height - imageDimensions.height,
        );
        const absoluteWidthDifference = Math.abs(width - imageDimensions.width);

        const sameHeight =
          absoluteHeightDifference > 0 && absoluteHeightDifference <= 1;
        const sameWidth =
          absoluteWidthDifference > 0 && absoluteWidthDifference <= 1;

        let interacted = false;
        if (!sameHeight || !sameWidth) interacted = true;

        handleVisualSearch(
          {
            x: Math.round(x),
            y: Math.round(y),
            x1: Math.round(width) + Math.round(x),
            y1: Math.round(height) + Math.round(y),
            file: newFile,
            interacted,
          },
          { flatResponse: true },
        );
      }, "image/jpeg");
    }
  };

  const handleCropData = () => {
    if (isVQLUpgradeEnabled) {
      getCropDataVQL();
    } else {
      getCropData();
    }
  };

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.src = URL.createObjectURL(file);
  }, [file]);

  useHotkeys("Enter", () => {
    if (!isLoading) {
      handleCropData();
    }
  });

  return (
    <div className={classNames(styles.border, styles.cropper)}>
      {isVQLUpgradeEnabled ? (
        <VLCropper
          ref={vlCropperRef}
          src={image}
          className={styles.cropperSize}
          autoCrop={true}
          cropBoxSizeRatio={0.95}
        />
      ) : (
        <Cropper
          ref={cropperRef}
          style={{ height: "400px", width: "100%" }}
          zoomTo={0}
          initialAspectRatio={1}
          preview=".img-preview"
          src={image}
          viewMode={1}
          minCropBoxHeight={70}
          minCropBoxWidth={70}
          background={false}
          responsive
          autoCropArea={100}
          checkOrientation
          guides
        />
      )}
      {isVQLUpgradeEnabled ? (
        <div className={styles.findSimilarButtons}>
          <IconButton
            className={styles.findSimilarButton}
            icon={<VisualSimilarityButtonIcon />}
            onClick={handleCropData}
            text={"Find Similar"}
            variant={"success"}
          />
          {isVqlExcludeEnabled && (
            <IconButton
              variant={"danger"}
              className={styles.findSimilarButton}
              icon={<VisualSimilarityButtonIcon />}
              onClick={handleCropData}
              text={"Exclude Similar"}
            />
          )}
        </div>
      ) : (
        <VLButton
          className={styles.findSimilarButton}
          type="primary"
          icon={<VisualSimilarityButtonIcon />}
          size="middle"
          onClick={handleCropData}
          loading={isLoading}
        >
          Find Similar
        </VLButton>
      )}
    </div>
  );
};

export const ImageSearch = ({
  className,
  onUpload,
  initialFile,
}: ImageSearchProps) => {
  const {
    file,
    setFile,
    handleVisualSearch,
    handleVQLVisualSearch,
    isVQLUpgradeEnabled,
  } = useImageUpload(onUpload);

  useEffect(() => {
    if (initialFile) {
      setFile(initialFile);
    }
  }, [initialFile, setFile]);

  return (
    <div className={classNames(styles.ImageSearch, className)}>
      {!file ? (
        <Upload setFile={setFile} />
      ) : (
        <Crop
          file={file}
          handleVisualSearch={
            isVQLUpgradeEnabled ? handleVQLVisualSearch : handleVisualSearch
          }
        />
      )}
    </div>
  );
};
